/**
 * Sign-out.
 *
 * Clears local session state first, then tells the server. The ORDER matters:
 * revoking the session can fail (offline, expired token, already revoked), and
 * waiting on it would leave the user staring at an authenticated screen while a
 * request they already asked to cancel is in flight.
 *
 * The local state is cleared regardless of the server outcome, and the server
 * failure is reported rather than swallowed — a user who believes they signed
 * out must BE signed out locally, and must be told if the server did not agree.
 */
import { emitAuthEvent } from '@/api/client/auth-events';
import { broadcastAuthSync } from '@/app/session/multi-tab';
import { resetSession } from '@/app/session/session-state';

export type SignOutReason =
  | 'user-initiated'
  | 'idle-timeout'
  | 'session-expired'
  | 'cross-tab'
  | 'unauthorized';

export type SignOutResult = {
  readonly localStateCleared: true;
  readonly serverAcknowledged: boolean;
};

/**
 * Revokes the session on the server. Injected so this module carries no
 * transport dependency and can be tested without fetch.
 */
export type ServerRevoker = () => Promise<void>;

export async function performSignOut(
  revoke: ServerRevoker,
  reason: SignOutReason = 'user-initiated',
): Promise<SignOutResult> {
  // 1. Local state first — always, and unconditionally.
  resetSession();
  emitAuthEvent('signed-out');

  // 2. Only a genuine user action is worth telling other tabs about. An idle
  //    timeout in one background tab should not sign the user out everywhere.
  if (reason === 'user-initiated') {
    broadcastAuthSync({ type: 'signed-out' });
  }

  // 3. Tell the server, and report honestly whether it agreed.
  try {
    await revoke();
    return { localStateCleared: true, serverAcknowledged: true };
  } catch {
    return { localStateCleared: true, serverAcknowledged: false };
  }
}